import React, { useEffect, useState } from "react";
import { t } from "i18next";

import { ICalendar, ICalendarData, ICalendarDataEvent } from "../../../interfaces/event/calendar"

import Typography from "../../shared/typography/Typography";
import BaseToggle from "../../shared/BaseToggle";
import Button from "../../shared/button/Button";
import Input from "../../shared/input/Input";
import PageTitle from "../../shared/pageTitle/PageTitle";
import { InputDate } from "../../shared-v2/InputDate";
import { Subtitle } from "../../shared-v2/Subtitle";
import { Tag } from "../../shared-v2/Tag";
import { Card } from "../../shared-v2/Card";
import { InputTime } from "../../shared-v2/InputTime";
import { IFile, UploadImage } from "../../shared-v2/UploadImage";

import { Checkbox } from "antd";

import { breakpointSize } from "../../../theme/mediaQuerie";
import { 
  Column, 
  InputGroup, 
  NewDayContainer, 
  Row, 
  RowGrid,
  ToggleContainer, 
  FormContainer,
  ListDays,
  ListEvents,
  CardContent
} from "./style";
import colors from "../../../theme/colors";

interface IProps {
  calendar: ICalendar;
  setCalendar: React.Dispatch<React.SetStateAction<ICalendar>>;
}

const isMobile = window.innerWidth <= breakpointSize.Sm;

const EventDetails = ({ item }: {item: ICalendarDataEvent}) => (
  <CardContent>
    <div>
      <div>
        <Typography color="body" type="headingXXS">{t("startDate") + ": "}</Typography>
        <Typography color="body" type="paragraphXS">
          {`${item.start.split("-")[2]}/${item.start.split("-")[1]}/${item.start.split("-")[0]}`}
          {` a `}
          {`${item.end.split("-")[2]}/${item.end.split("-")[1]}/${item.end.split("-")[0]}`}
        </Typography>
      </div>
      <div>
        <Typography color="body" type="headingXXS">{t("time") + ": "}</Typography>
        <Typography color="body" type="paragraphXS">{item.time.replace(":", "h")}</Typography>
      </div>
    </div>
    <div>
      <div>
        <Typography color="body" type="headingXXS">{t("title") + ": "}</Typography>
        <Typography color="body" type="paragraphXS">{item.title}</Typography>
      </div>
      <div>
        <Typography color="body" type="headingXXS">{t("description") + ": "}</Typography>
        <Typography color="body" type="paragraphXS">{item.description}</Typography>
      </div>
    </div>
  </CardContent>
);

const EventCard = ({item, onDelete, style}: {item: ICalendarDataEvent, onDelete: () => void, style: React.CSSProperties}) => (
  <Card style={style} customBackground="background" onDelete={onDelete} title={item.title} key={item.title}>
    <EventDetails item={item} />
  </Card>
);

const CalendarEvents = ({events, handleRemoveEvent, indexDay }: {events: ICalendarDataEvent[], handleRemoveEvent: (indexEvent: number, indexDay: number) => void, indexDay: number}) => (
  <>
    {events.map((item: ICalendarDataEvent, indexEvent: number) => (
      <EventCard
        style={indexEvent !== 0 ? { marginTop: '16px' } : {}}
        onDelete={() => handleRemoveEvent(indexEvent, indexDay)}
        item={item}
        key={item.title}
      />
    ))}
  </>
);

export const Calendar = ({calendar, setCalendar}: IProps) => {
  const [newEvent, setNewEvent] = useState<ICalendarDataEvent>({
    description: "",
    end: new Date().toISOString().split('T')[0],
    start: new Date().toISOString().split('T')[0],
    time: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
    title: "",
    image: "",
  });
  const [newDay, setNewDay] = useState("");
  const [selectedDay, setSelectedDay] = useState<{title: string, index: number}>();
  const [file, setFile] = useState<IFile | null>(null);

  useEffect(() => {
    if (selectedDay && selectedDay.index >= calendar.itens.length) {
      setSelectedDay(undefined);
    }
  }, [calendar, selectedDay]);

  const updateCalendar = (attr: keyof ICalendar, value: boolean) => {
    setCalendar(prev => ({...prev, [attr]: value}));
  }

  const updateNewDay = (attr: keyof ICalendarData, value: string) => {
    setNewDay(value);
  }

  const updateNewEvent = (attr: keyof ICalendarDataEvent  , value: string) => {
    setNewEvent(prev => ({...prev, [attr]: value}));
  }

  const handleAddDay = () => {
    let newItens = calendar.itens;
    newItens.push({title: newDay, events: []});
    setCalendar(prev => ({...prev, itens: newItens}));

    if(newItens.length === 1) {
      setSelectedDay({title: newDay, index: 0});
    }

    setNewDay("");
  }

  const handleRemoveDay = (index: number) => {
    let newItens = calendar.itens;
    newItens.splice(index, 1);
    setCalendar(prev => ({...prev, itens: newItens}));
  }

  const handleAddEvent = () => {
    if(!selectedDay) return;

    let newEvents = calendar.itens[selectedDay.index].events;
    newEvents.push(newEvent);
    let newItens = calendar.itens;
    newItens[selectedDay.index].events = newEvents;
    setCalendar(prev => ({...prev, itens: newItens}));

    setNewEvent((previous) => {
      return { ...previous, title: "", description: "", webibanr: "" };
    });
  }

  const handleRemoveEvent = (indexEvent: number, indexDay: number) => {

    let newEvents = calendar.itens[indexDay].events;
    newEvents.splice(indexEvent, 1);

    let newItens = calendar.itens;
    newItens[indexDay].events = newEvents;
    
    let newState = {...calendar, itens: newItens};
    setCalendar(prev => ({...prev, itens: newItens}));
  }

  return (
    <>
      <Row>
        { !isMobile && <PageTitle style={{margin: 0}}>{t("dashboard.event.calendar.title")}</PageTitle> }
        <ToggleContainer>
          <Typography type="paragraphS" color="title">{t("dashboard.event.displaySection")}</Typography>
          <BaseToggle
            initialCheck={calendar.menu ?? false} 
            onChange={(checked: boolean) => updateCalendar("menu", checked)} 
          />
        </ToggleContainer>
      </Row>

      <RowGrid>
        <Column style={isMobile ? {} : {padding: '0 16px'}}>
          <NewDayContainer>
            <InputGroup>
              <Subtitle>{t("title")}</Subtitle>
              <Input 
                placeholder={t("dashboard.event.calendar.day.placeholder")!} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateNewDay("title", e.target.value)}
                value={newDay} 
              />
            </InputGroup>
            <Button
              onClick={handleAddDay} 
              size="small"
              variant="primary"
              enable={newDay.trim() !== ""}
              fullWidth
              typographyType="paragraphXXS"
              tooltip={newDay.trim() !== "" ? "" : t("dashboard.event.calendar.tooltipDay")!}
            >
              {t("save")}
            </Button>
          </NewDayContainer>

          {isMobile && (
            <>
              <Subtitle>{t("calendar")}</Subtitle>
              <ListDays>
                {calendar.itens.length > 0 && (
                calendar.itens.map((item: ICalendarData, index: number) => (
                  <Tag
                    title={item.title} 
                    onRemove={() => {handleRemoveDay(index)}} 
                    onSelect={() => setSelectedDay({title: item.title, index: index})} 
                    key={index}
                    selected={selectedDay?.index === index}
                  />
                )))}
              </ListDays>
           </>
          )}

          <FormContainer>
            <InputGroup>
              <Subtitle>{t("startDate")}</Subtitle>
              <InputDate 
                date={newEvent.start} 
                setDate={(value) => {updateNewEvent("start", value)}} 
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>{t("endDate")}</Subtitle>
              <InputDate 
                date={newEvent.end} 
                setDate={(value) => {updateNewEvent("end", value)}} 
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>{t("time")}</Subtitle>
              <InputTime  time={newEvent.time} setTime={(value) => {updateNewEvent("time", value)}} />
            </InputGroup>

            <InputGroup>
              <Subtitle>{t("title")}</Subtitle>
              <Input 
                maxLength={50} 
                value={newEvent.title} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateNewEvent("title", e.target.value)} 
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>{t("description")}</Subtitle>
              <Input 
                maxLength={50} 
                value={newEvent.description} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateNewEvent("description", e.target.value)} 
              />
            </InputGroup>

            <InputGroup>
              <Subtitle>{t("linkWebinar")}</Subtitle>
              <Input 
                placeholder="Link"
                value={newEvent.webinar ?? ""} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateNewEvent("webinar", e.target.value)} 
              />
              <Checkbox >
                <Typography type="paragraphXXXS" color="label" >
                  {t("dashboard.event.calendar.importWebinarData")}
                </Typography>
              </Checkbox>
            </InputGroup>

            <UploadImage 
              id="calendar-upload-image" 
              maxImageSize={1024 * 1024 * 10}
              file={file} 
              setFile={(file) => {updateNewEvent("image", file?.url ?? ""); setFile(file)}}
              title={t("image")} 
              style={isMobile ? {backgroundColor: colors.line} : {}}
            />

            <Button 
              onClick={handleAddEvent}
              tooltip={
                selectedDay ? 
                  ( newEvent.title.trim() !== "" ? 
                    "" : 
                    t("dashboard.event.calendar.tooltipEventTitle")!) : 
                  t("dashboard.event.calendar.tooltipEventDay")!
                }
              enable={selectedDay !== undefined && (newEvent.title.trim() !== "")}
              style={{minWidth: '180px', marginLeft: "auto", marginTop: "16px"}}
              size="small"
              fullWidth={isMobile}
              variant="primary"
              typographyType="paragraphXXS"
            >
              {t("add")}
            </Button>
          </FormContainer>
        </Column>

        <Column>
         {!isMobile && (
           <>
            <Subtitle>{t("calendar")}</Subtitle>
            <ListDays>
              {calendar.itens.length > 0 && (
              calendar.itens.map((item: ICalendarData, index: number) => (
                <Tag
                  title={item.title} 
                  onRemove={() => {handleRemoveDay(index)}} 
                  onSelect={() => setSelectedDay(prev => (
                    selectedDay?.index === index ? undefined : {title: item.title, index: index}
                  ))} 
                  key={item.title}
                  selected={selectedDay?.index === index}
                />
              )))}
            </ListDays>
           </>
         )}

          <ListEvents>
            {selectedDay && calendar.itens[selectedDay.index] ? (
              calendar.itens[selectedDay.index].events.length > 0 ? (
                <CalendarEvents
                  events={calendar.itens[selectedDay.index].events}
                  handleRemoveEvent={handleRemoveEvent}
                  indexDay={selectedDay.index}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '170px' }}>
                  <Typography type="paragraphS" color="label">
                    {t("dashboard.event.calendar.noEvents")}
                  </Typography>
                </div>
              )
            ) : (
              calendar.itens.map((item, indexDay) => (
                <CalendarEvents events={item.events} handleRemoveEvent={handleRemoveEvent} indexDay={indexDay} key={indexDay} />
              ))
            )}
          </ListEvents>
        </Column>
      </RowGrid>
    </>
  )
}